import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSkills } from 'api/queries/marketing-capabilities/skills/useSkills'
import { setExploreLearningsSkill, setExploreLearningsSkillNames, useExploreLearnings } from 'store/ExploreLearnings'

import { EmptyResult } from '../components/EmptyResult'
import ExploreMoreSkills from '../components/ExploreMoreSkills'
import { SkillBanner } from '../components/SkillBanner'
import { SkillCard } from '../components/SkillCard'
import { SkillSearchSection } from '../components/SkillSearchSection'

export const SkillListPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { data: skillList, isLoading } = useSkills()

  const search = useExploreLearnings(state => state.search)
  const skillState = useExploreLearnings(state => state.skill)

  const skills = skillList?.skills || []
  const totalCount = skillList?.meta?.total ?? skills.length ?? 0

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const skillsParam = params.get('skills')
    const skillLabels = skillsParam ? skillsParam.split(',').map(s => s.trim()) : []

    if (skillList) {
      const skillListData = Array.isArray(skillList) ? skillList : skillList?.data || skillList?.skills || []
      const validSkills = skillLabels
        .map(label => skillListData.find((skill: any) => skill.name === label))
        .filter((skill: any): skill is any => !!skill)

      const validSkillIds = validSkills.map((skill: any) => skill.id)
      const validSkillNames = validSkills.map((skill: any) => skill.name)

      if (validSkillIds.length > 0 && JSON.stringify(validSkillIds) !== JSON.stringify(skillState)) {
        setExploreLearningsSkill(validSkillIds)
        setExploreLearningsSkillNames(validSkillNames)
      }
    }
  }, [location.search, skillList, skillState])

  const getSkillObject = (skill: any) => {
    return (
      <div className="" key={skill.id}>
        {skill.banner !== 1 ? (
          <SkillCard name={skill.name} image={skill.iconUrl} id={skill.id} />
        ) : (
          <SkillBanner name={skill.name} description={skill.description} image={skill.iconUrl} id={skill.id} />
        )}
      </div>
    )
  }

  return (
    <>
      {skillList && skillList.skills && skillList?.skills?.length === 0 && !isLoading && <EmptyResult />}
      {search && totalCount > 0 && (
        <div className="w-[1120px] mx-auto">
          <p className="text-[16px] leading-[24px] font-TCCCText mt-[40px] mb-[16px]">
            <b className="text-[20px] font-semibold leading-[32px]">{totalCount}</b> skill{totalCount > 1 ? 's' : ''}{' '}
            matching with “{search}”
          </p>
        </div>
      )}

      {!search ? (
        !isLoading &&
        skillList &&
        skillList.length > 0 && (
          <div className="py-16 w-[1120px] mx-auto flex flex-wrap justify-between gap-y-8">
            {skillList.map((skill: any) => getSkillObject(skill))}
          </div>
        )
      ) : (
        <>
          {!isLoading && skillList && skillList?.skills && skillList?.skills?.length > 0 && (
            <div className="mt-10 w-[1120px] mx-auto flex flex-wrap justify-between gap-y-8">
              {skillList.skills.map((skill: any, index: number) => (
                <SkillSearchSection
                  key={skill.id}
                  skill={skill}
                  border={index < skillList.skills.length - 1}
                  resources={skill.resources}
                  practitioners={skill.practitioners}
                  goResources={() => {
                    setExploreLearningsSkill([skill.id])
                    setExploreLearningsSkillNames([skill.name])
                    navigate(`/explore-learnings/resources?skills=${skill.id}`)
                  }}
                  goPractitioners={() => {
                    setExploreLearningsSkill([skill.id])
                    setExploreLearningsSkillNames([skill.name])
                    navigate(`/explore-learnings/community?skills=${skill.id}`)
                  }}
                />
              ))}
            </div>
          )}
          {skillList?.exploreMoreSkills && (
            <ExploreMoreSkills exploreMoreSkills={skillList.exploreMoreSkills} getSkillObject={getSkillObject} />
          )}
        </>
      )}
    </>
  )
}
